// Public landing page — no auth, live pages only. TEST/Studio UI never shown here.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Renderer } from "@/pages/studio/landing/LandingStudio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = (u) => (u || "").replace("/api/landing/assets/", "/api/public/landing-assets/");

function useDevice() {
    const q = () => (window.matchMedia("(max-width: 767px)").matches || new URLSearchParams(window.location.search).get("preview") === "mobile" ? "mobile" : "desktop");
    const [d, setD] = useState(q);
    useEffect(() => { const on = () => setD(q()); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); }, []);
    return d;
}

export default function PublicLanding() {
    const device = useDevice();
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [missing, setMissing] = useState(false);
    useEffect(() => {
        axios.get(`${API}/public/landing/${slug}`).then(({ data }) => {
            if (data.redirect_to) { window.location.replace(`/${data.redirect_to}`); return; }
            data.brand_logo_url = pub(data.brand_logo_url);
            data.sections = data.sections.map((s) => ({ ...s, content: { ...s.content,
                image_url: pub((s.content || {}).image_url),
                images: ((s.content || {}).images || []).map((i) => ({ ...i, url: pub(i.url) })) } }));
            setP(data);
            if (data.redirect_to_domain) {
                window.location.replace(`https://${data.redirect_to_domain}${window.location.pathname === `/${slug}` ? "/" : window.location.pathname}${window.location.search}`);
                return;
            }
            const pre = document.getElementById("merchant-prerender");
            if (pre) pre.remove();
            const qs = new URLSearchParams(window.location.search);
            if (qs.get("utm_medium") === "qr" && data.qr_tracking && !window.__qrScanSent) {
                window.__qrScanSent = true;
                axios.post(`${API}/public/landing/${slug}/scan`).catch(() => {});
            }
            const ensureP = (prop, content) => { let m = document.head.querySelector(`meta[property="${prop}"]`); if (!m) { m = document.createElement("meta"); m.setAttribute("property", prop); document.head.appendChild(m); } m.setAttribute("content", content); };
            ensureP("og:site_name", data.seo?.site_name || data.client_name);
            ensureP("og:url", data.canonical || window.location.href.split("?")[0]);
            if (data.favicon?.urls) {
                const u = data.favicon.urls["32"] || data.favicon.urls.ico || Object.values(data.favicon.urls)[0];
                if (u) { let l = document.head.querySelector('link[rel="icon"]'); if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); } l.href = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`; }
            }
            document.title = data.seo?.title || `${data.client_name}${data.offer ? ` | ${data.offer}` : ""}`;
            // per-client favicon — the platform icon must never appear on client pages
            const setIcon = (rel, href, sizes, type) => {
                let l = document.head.querySelector(`link[rel="${rel}"]${sizes ? `[sizes="${sizes}"]` : ""}`);
                if (!l) { l = document.createElement("link"); l.rel = rel; if (sizes) l.setAttribute("sizes", sizes); document.head.appendChild(l); }
                if (type) l.type = type; l.href = href;
            };
            document.head.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"], link[rel="shortcut icon"]').forEach((l) => l.remove());
            const NEUTRAL = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Ccircle cx='8' cy='8' r='6.5' fill='none' stroke='%23999' stroke-width='1.6'/%3E%3C/svg%3E";
            const fav = data.favicon;
            if (fav?.urls?.svg) {
                setIcon("icon", `${process.env.REACT_APP_BACKEND_URL}${fav.urls.svg}?v=${fav.version}`, "", "image/svg+xml");
            } else if (fav?.urls) {
                const v = `?v=${fav.version}`;
                if (fav.urls.s32) setIcon("icon", `${process.env.REACT_APP_BACKEND_URL}${fav.urls.s32}${v}`, "32x32", "image/png");
                if (fav.urls.s48) setIcon("icon", `${process.env.REACT_APP_BACKEND_URL}${fav.urls.s48}${v}`, "48x48", "image/png");
                if (fav.urls.s180) setIcon("apple-touch-icon", `${process.env.REACT_APP_BACKEND_URL}${fav.urls.s180}${v}`, "180x180");
            } else {
                setIcon("icon", NEUTRAL, "", "image/svg+xml");
            }
            const ensure = (sel, make) => document.head.querySelector(sel) || document.head.appendChild(make());
            if (data.og_image_url) {
                const og = ensure('meta[property="og:image"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:image"); return m; });
                og.setAttribute("content", `${process.env.REACT_APP_BACKEND_URL}${data.og_image_url}`);
            }
            const ogt = ensure('meta[property="og:title"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:title"); return m; });
            ogt.setAttribute("content", document.title);
            const md = ensure('meta[name="description"]', () => Object.assign(document.createElement("meta"), { name: "description" }));
            md.setAttribute("content", data.seo?.description || data.description || "");
            const robots = ensure('meta[name="robots"]', () => Object.assign(document.createElement("meta"), { name: "robots" }));
            robots.setAttribute("content", data.indexable ? "index, follow" : "noindex, nofollow");
            let canon = document.head.querySelector('link[rel="canonical"]');
            if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
            canon.href = data.canonical;
        }).catch(() => {
            setMissing(true);
            document.title = "Page not found — slowcafe.my";
            let robots = document.head.querySelector('meta[name="robots"]');
            if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
            robots.setAttribute("content", "noindex, nofollow");
        });
    }, [slug]);
    if (missing) return <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm" data-testid="landing-404">Page not found.</div>;
    if (!p) return null;
    return <div data-testid="public-landing"><Renderer p={p} bp={p.blueprint} device={device} /></div>;
}
